import { useState, useEffect } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import FilePreviewModal from "./FilePreviewModal";

const UserFiles = () => {
  const { user, addFile, getUserData } = useAuth();
  const [fileType, setFileType] = useState("images");
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const data = getUserData();
    if (data?.files?.[fileType]) {
      setFiles(data.files[fileType]);
    }
  }, [user, fileType, getUserData]);

  const handleFileUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files);
    await processFiles(uploadedFiles);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    await processFiles(droppedFiles);
  };

  const processFiles = async (filesToProcess) => {
    for (const file of filesToProcess) {
      if (
        (fileType === "images" && file.type.startsWith("image/")) ||
        (fileType === "videos" && file.type.startsWith("video/")) ||
        (fileType === "documents" &&
          (file.type.startsWith("application/") || file.type === "text/plain"))
      ) {
        const reader = new FileReader();

        reader.onload = async (e) => {
          const newFile = {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: file.name,
            type: file.type,
            size: file.size,
            uploadedAt: new Date().toISOString(),
            data: e.target.result,
          };

          await addFile(fileType, newFile);
          setFiles((prev) => [...prev, newFile]);
        };

        reader.readAsDataURL(file);
      }
    }
  };

  return (
    <LayoutGroup>
      <div className="files-container">
        <AnimatePresence>
          {showPreview && (
            <FilePreviewModal
              file={selectedFile}
              onClose={() => setShowPreview(false)}
            />
          )}
        </AnimatePresence>

        <div className="files-header">
          <motion.h2 layout>My Files</motion.h2>
          <motion.div layout className="file-type-selector">
            {["images", "videos", "documents"].map((type) => (
              <motion.button
                key={type}
                className={fileType === type ? "active" : ""}
                onClick={() => setFileType(type)}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </motion.button>
            ))}
          </motion.div>
        </div>

        <motion.div
          className={`upload-area ${isDragging ? "dragging" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.02 }}
        >
          <input
            type="file"
            id="file-upload"
            multiple
            onChange={handleFileUpload}
            style={{ display: "none" }}
            accept={
              fileType === "images"
                ? "image/*"
                : fileType === "videos"
                ? "video/*"
                : "*"
            }
          />
          <label htmlFor="file-upload">
            <motion.div className="upload-content" whileHover={{ scale: 1.05 }}>
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4f46e5"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="17 8 12 3 7 8"></polyline>
                  <line x1="12" y1="3" x2="12" y2="15"></line>
                </svg>
              </motion.div>
              <motion.p>Drag & drop files here or click to browse</motion.p>
              <motion.p className="upload-hint">
                Supported:{" "}
                {fileType === "images"
                  ? "Images"
                  : fileType === "videos"
                  ? "Videos"
                  : "Documents"}
              </motion.p>
            </motion.div>
          </label>
        </motion.div>

        <motion.div layout className="files-grid">
          <AnimatePresence>
            {files.length > 0 ? (
              files.map((file) => (
                <motion.div
                  key={file.id}
                  className="file-card"
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ type: "spring", damping: 20 }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setSelectedFile(file);
                    setShowPreview(true);
                  }}
                >
                  <div className="file-preview">
                    {fileType === "images" ? (
                      <motion.img
                        src={file.data}
                        alt={file.name}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      />
                    ) : fileType === "videos" ? (
                      <motion.video
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        <source src={file.data} type={file.type} />
                      </motion.video>
                    ) : (
                      <motion.div
                        className="file-icon"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#4f46e5"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                        </svg>
                      </motion.div>
                    )}
                  </div>
                  <motion.div className="file-info">
                    <h4>{file.name}</h4>
                    <p>{(file.size / 1024).toFixed(2)} KB</p>
                    <p>{new Date(file.uploadedAt).toLocaleDateString()}</p>
                  </motion.div>
                </motion.div>
              ))
            ) : (
              <motion.div
                className="empty-state"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <p>No files found. Upload your first {fileType}!</p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </LayoutGroup>
  );
};

export default UserFiles;
